import { CONFIG } from '../data/config';
import {
  FED_PRINCIPLE,
  FED_TRAITS,
  HCD_RECRUIT,
  HCD_TRAINING,
  SUCCESSION_CASES,
  BACK_OFFICE,
} from '../data/federation';
import { FED_ICONS } from './federation/icons';
import HiringChart from './federation/HiringChart';
import ImgSlot from './ui/ImgSlot';

export default function Federation() {
  return (
    <section id="federation" className="section section--paper">
      <div className="container">
        <div className="eyebrow">
          <span className="bar" />
          <span>Technical Services Federation</span>
        </div>

        <div className="fed-intro">
          <div>
            <h2 className="fed-term">
              Technical Services
              <br />
              <span className="em-red">Federation.</span>
            </h2>
          </div>
          <div className="fed-principle">
            <div className="fed-principle-en">{FED_PRINCIPLE.en}</div>
            <p>{FED_PRINCIPLE.body}</p>
          </div>
        </div>

        {/* TSF — three defining characteristics */}
        <div className="fed-traits">
          <div className="fed-traits-head">
            <span className="bar" />
            <span>
              Three characteristics of <em>federation management</em>
            </span>
          </div>
          <div className="fed-traits-grid">
            {FED_TRAITS.map((t) => {
              const Icon = FED_ICONS[t.icon];
              return (
                <div className="fed-trait" key={t.n}>
                  <div className="fed-trait-icon-wrap">
                    <Icon />
                  </div>
                  <h3 className="fed-trait-title">{t.title}</h3>
                  <p className="fed-trait-body">{t.body}</p>
                </div>
              );
            })}
          </div>
        </div>

        {/* ─ Pillar A: Human Capital Development ───────────────────── */}
        <div className="fed-block">
          <div className="fed-block-head">
            <h3 className="h3 fed-block-title">Human Capital Development</h3>
            <p className="fed-block-lede">
              For a technical-services business, talent is the lifeline. Solo
              recruitment and training is brutal in a tight labour market — Mystar
              Engineering runs both as group-scale infrastructure for its federation
              companies.
            </p>
          </div>

          <div className="fed-block-body">
            <div className="hcd-recruit">
              <div className="fed-sub-head">
                <div className="fed-sub-tag">Recruitment support</div>
                <div className="fed-sub-title">
                  Three levers, only available at group scale.
                </div>
              </div>
              <div className="hcd-cards">
                {HCD_RECRUIT.map((c) => (
                  <div className="hcd-card" key={c.n}>
                    <h4 className="h4">{c.title}</h4>
                    <p>{c.body}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="hcd-hiring">
              <div className="fed-sub-head">
                <div className="fed-sub-tag">Group-wide hires</div>
                <div className="fed-sub-title">Compounding, year after year.</div>
              </div>
              <div className="hcd-chart-wrap">
                <HiringChart />
                <p className="hcd-chart-note">
                  Group-wide new hires per fiscal year. Source: Mystar Engineering
                  Group.
                </p>
              </div>
            </div>
          </div>

          <div className="fed-block-body">
            <div className="hcd-training">
              <div className="fed-sub-head">
                <div className="fed-sub-tag">Training support</div>
                <div className="fed-sub-title">
                  Moving past <em>“watch and learn”</em> — toward structured,
                  repeatable engineer development.
                </div>
              </div>
              <p className="hcd-training-body">
                A structured curriculum replaces the old apprentice-style training,
                accelerating the development of the engineers who carry the business.
                Six standing programs run continuously across the group.
              </p>
              <ul className="training-grid">
                {HCD_TRAINING.map((t, i) => (
                  <li key={i}>
                    <span className="t-l">{t}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="hcd-center">
              <div className="fed-sub-head">
                <div className="fed-sub-tag">Facility</div>
                <div className="fed-sub-title">
                  ME Technical Centre · Chiba Research Park
                </div>
              </div>
              <ImgSlot
                ratio="4/3"
                src="assets/facilities/me-technical-center.jpg"
                alt="ME Technical Centre exterior at dusk"
              />
              <p className="hcd-center-body">
                A large-scale facility with mock production lines, residential training
                quarters, and live equipment supplied by major electrical-equipment and
                plant-engineering manufacturers — designed for hands-on, domain-specific
                engineer training.
              </p>
            </div>
          </div>
        </div>

        {/* ─ Pillar B: Management Support ──────────────────────────── */}
        <div className="fed-block">
          <div className="fed-block-head">
            <h3 className="h3 fed-block-title">Management Support</h3>
            <p className="fed-block-lede">
              For a federation company to run independently, the founder needs a
              successor and the operation needs functioning back-office muscle. Mystar
              Engineering's onboarding teams cover both — drawing on the M&amp;A and
              operating playbook the group has compiled across {CONFIG.maDealsTotal}{' '}
              deals.
            </p>
          </div>

          <div className="succession">
            <div className="fed-sub-head">
              <div className="fed-sub-tag">Succession support</div>
              <div className="fed-sub-title">Two paths, four documented patterns.</div>
            </div>
            <div className="succession-cols">
              {SUCCESSION_CASES.map((s, i) => (
                <div className="succession-col" key={i}>
                  <div className="succession-col-title">{s.type}</div>
                  <div className="succession-cases">
                    {s.cases.map((c, j) => (
                      <div className="case-card" key={j}>
                        <p>{c.body}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="back-office">
            <div className="bo-half">
              <div className="fed-sub-head">
                <div className="fed-sub-tag">Back-office support</div>
                <div className="fed-sub-title">
                  Five group-run functions, modular by request.
                </div>
              </div>
              <p className="bo-body">
                So federation companies focus on engineering, Mystar Engineering
                provides shared back-office capacity in five functions, tailored to
                each company's stage and existing strengths.
              </p>
              <ul className="bo-list">
                {BACK_OFFICE.map((b, i) => (
                  <li key={i}>{b}</li>
                ))}
              </ul>
            </div>
            <div className="bo-half">
              <div className="fed-sub-head">
                <div className="fed-sub-tag">Sales &amp; procurement support</div>
                <div className="fed-sub-title">
                  Customer and supplier networks, shared across the federation.
                </div>
              </div>
              <p className="bo-body">
                Cross-company referrals and shared procurement contracts compound
                across the group — expanding the customer base and reducing cost on
                inputs that no single company could move alone.
              </p>
              <div className="bo-flow">
                <span className="bo-node">Group Co. A</span>
                <span className="bo-arrow">→ referral →</span>
                <span className="bo-node">Group Co. B</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
